import { ReactNode, Suspense, lazy, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { LogOut, User } from 'lucide-react';
import { AlertsBell } from '@/components/alerts/AlertsBell';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { GlobalSearch } from '@/components/GlobalSearch';
import { BrandSwitcher } from '@/components/shell/BrandSwitcher';
import { CommandPalette } from '@/components/shell/CommandPalette';
import { TrialBanner, TrialLockout } from '@/components/TrialBanner';
import { ModerationBanner } from '@/components/common/ModerationBanner';
// Step 7 — FlowTokBot pulls framer-motion + recharts; defer it off the critical
// path. WhatsNewModal is interaction-gated so deferring is also safe.
const FlowTokBot = lazy(() => import('@/components/FlowTokBot').then(m => ({ default: m.FlowTokBot })));
const WhatsNewModal = lazy(() => import('@/components/whats-new/WhatsNewModal').then(m => ({ default: m.WhatsNewModal })));
import { ThemeToggle } from '@/components/ThemeToggle';
import { UpdatesBellButton } from '@/components/whats-new/UpdatesBellButton';

import { useWalkthrough } from '@/hooks/useWalkthrough';

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const { user, role, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useWalkthrough();

  return (
    <SidebarProvider>
      <a href="#dashboard-main" className="skip-to-content">Skip to content</a>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-[56px] flex items-center justify-between gap-2 px-3 sm:px-4 lg:px-6 glass-header sticky top-0 z-20">
            <div className="flex items-center gap-2 min-w-0 flex-1 max-w-2xl">
              <SidebarTrigger className="ftos-header-icon h-8 w-8 rounded-md -ml-1 shrink-0" />
              <div className="hidden md:block h-5 w-px bg-white/10 mx-1 shrink-0" />
              <BrandSwitcher />
              <div className="hidden md:block h-5 w-px bg-white/10 mx-1 shrink-0" />
              <div className="min-w-0 flex-1"><GlobalSearch /></div>
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
              <UpdatesBellButton />
              <AlertsBell />
              <ThemeToggle />
              <div className="hidden sm:block h-5 w-px bg-white/10 mx-2" />
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="ftos-header-icon h-8 gap-2 px-1.5 rounded-md">
                    <div className="h-6 w-6 rounded-full flex items-center justify-center shrink-0 ring-1 ring-white/15" style={{ background: 'linear-gradient(135deg, #A855F7 0%, #22D3EE 100%)', boxShadow: '0 0 12px -2px rgba(34,211,238,0.5)' }}>
                      <User className="h-3 w-3 text-white" />
                    </div>
                    <span className="hidden lg:inline font-medium text-[12.5px] max-w-[180px] truncate text-white/85">{user?.email}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-52 p-1.5 surface-raised">
                  <ThemeToggle variant="inline" />
                  <div className="h-px my-1 bg-border" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-secondary"
                    onClick={() => { setOpen(false); signOut(); }}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </PopoverContent>
              </Popover>
            </div>
          </header>
          <TrialBanner />
          <TrialLockout />
          <ModerationBanner />
          <main id="dashboard-main" className="flex-1 overflow-auto" aria-label="Main content">
            <div
              key={location.pathname}
              className="page-shell page-transition"
            >
              {children}
            </div>
          </main>
        </div>
      </div>
      <Suspense fallback={null}>
        <FlowTokBot />
        <WhatsNewModal />
      </Suspense>
      <CommandPalette />
    </SidebarProvider>
  );
}
